<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card
      class="q-dialog-plugin"
      style="min-width:60vw"
    >
      <q-card-section class="row">
        <div
          class="text-h6 text-ellipsis col"
        >
          {{ dialogTitle }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-form @submit="onSubmit">
        <q-card-section class="col-12 row q-col-gutter-md">
          <q-select
            v-model="editSessionActivity.type"
            :options="selectionDisplayedOptions"
            :label="!editSessionActivity.type
              ? $t('sessions.modals.sessionActivity.selectActivityPlaceholder') : undefined"
            :rules="[val => !!val || $t('sessions.modals.sessionActivity.selectActivityValidation')]"
            outlined
            emit-value
            map-options
            hide-bottom-space
            class="col-12 col-md-4"
          >
            <template #selected-item="scope">
              <div class="row no-wrap items-center q-gutter-x-sm">
                <q-icon
                  :name="scope.opt.icon"
                  size="sm"
                  color="primary"
                />
                <span>{{ scope.opt.label }}</span>
              </div>
            </template>
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
              >
                <q-item-section avatar>
                  <q-icon :name="scope.opt.icon" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <client-users-selector
            v-model="sessionActivityUser"
            :client="sessionActivityClient"
            :label="$t('sessions.modals.sessionActivity.labelStaff')"
            :readonly="!hasSessionActivityClient"
            class="col-12 col-md-8"
          />
        </q-card-section>
        <q-card-actions>
          <div
            v-if="isRequestingGeolocation || hasGeolocation"
            class="col row items-center no-wrap text-dark-variant"
          >
            <q-icon
              name="place"
              color="positive"
              size="xs"
              class="q-mr-xs"
            />
            <q-spinner-puff
              v-if="isRequestingGeolocation"
              class="q-mr-sm"
            />
            <span
              v-if="hasGeolocation"
              class="ellipsis"
            >{{ geolocation.place }}</span>
            <span
              v-else
              class="ellipsis"
            >{{ $t('signature.textRequestingGeolocation') }}</span>
          </div>
          <div
            v-else
            class="col"
          />
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
          />
          <q-btn
            flat
            :label="$t('sessions.modals.sessionActivity.buttonStartActivity')"
            :disable="!isFormValid"
            type="submit"
            color="primary"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>
<script>
import ClientUsersSelector from 'components/UI/ClientUsersSelector';
import { Timestamp } from 'firebase/firestore';
import { cloneDeep, get, pick } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import { geolocation as geolocationStore } from 'src/store/geolocation';

export default {

  name: 'StartSessionActivityDialog',

  components: { ClientUsersSelector },

  mixins: [SessionActivitiesMixin],

  props: {
    sessionActivity: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    lockUser: {
      type: Boolean,
      default: false,
    },
    excludedOptions: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { formatDate } = useTime();
    return { formatDate };
  },

  data() {
    return {
      sessionActivityDate: null,
      editSessionActivity: {
        user: null,
        userName: null,
        startTimestamp: null,
        endTimestamp: null,
        type: null,
        distance: null,
      },

      geolocation: null,

      startTimestampLimit: (d) => {
        return d <= this.formatDate(Date.now(), 'yyyy/MM/dd');
      },

      existingDisabledOptions: [],
    };
  },

  computed: {
    ...mapState(geolocationStore, ['isRequestingGeolocation']),

    dialogTitle() {
      if (this.title !== null) {
        return this.title;
      }
      return this.sessionActivity.id
        ? this.$t('sessions.modals.sessionActivity.dialogTitleEdit')
        : this.$t('sessions.modals.sessionActivity.dialogTitleAdd');
    },

    isFormValid() {
      return (!!this.editSessionActivity.user)
        && (this.editSessionActivity.startTimestamp instanceof Timestamp)
        && (this.sessionActivityDate instanceof Timestamp);
    },

    hasSessionActivityClient() {
      return this.sessionActivityClient !== null;
    },

    hasDistance() {
      return !!this.activityTypesSettings[this.editSessionActivity.type]?.hasDistance;
    },

    hasGeolocation() {
      return !!get(this.geolocation, 'place');
    },

    sessionActivityClient() {
      return get(this.editSessionActivity, 'client', null);
    },

    sessionActivityUser: {
      get() {
        return {
          user: get(this.editSessionActivity, 'user', null),
          userName: get(this.editSessionActivity, 'userName', null),
        };
      },
      set(value) {
        this.editSessionActivity.user = get(value, 'user', null);
        this.editSessionActivity.userName = get(value, 'userName', null);
      },
    },

    selectionDisplayedOptions() {
      const filteredActivityTypeOptions = this.sessionActivityTypeOptions.filter(({ value }) => {
        return !this.excludedOptions[value];
      });
      return [...filteredActivityTypeOptions, ...this.existingDisabledOptions];
    },
  },

  watch: {
    sessionActivity: {
      immediate: true,
      deep: true,
      handler(sessionActivity) {
        this.editSessionActivity = cloneDeep(sessionActivity);
        if (
          this.editSessionActivity?.type
          && !this.sessionActivityTypeOptions.find(({ value }) => value === this.editSessionActivity.type)
        ) {
          const { value, label, icon } = this.activityTypesSettings[this.editSessionActivity.type];
          this.existingDisabledOptions.push({ value, label, icon, disable: true });
        }
        this.sessionActivityDate = get(this.editSessionActivity, 'startTimestamp', null);
      },
    },
  },

  methods: {
    ...mapActions(geolocationStore, ['executeGeolocationCallback']),

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
      this.getGeolocation();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    async onSubmit() {
      this.editSessionActivity.startTimestamp = Timestamp.now();
      this.editSessionActivity.endTimestamp = null;
      this.editSessionActivity.startGeolocation = this.geolocation || null;

      this.$emit(
        'ok',
        pick(
          this.editSessionActivity,
          ['user', 'userName', 'startTimestamp', 'endTimestamp', 'type', 'startGeolocation'],
        ),
      );
      this.hide();
    },

    getGeolocation() {
      this.executeGeolocationCallback((geolocation) => {
        if (!this) {
          // The dialog was destroyed while waiting for the geolocation
          return;
        }
        this.geolocation = geolocation;
      });
    },

  },

};
</script>
